import { useEffect, useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { Folder, FolderOpen, Flame } from "lucide-react";
import { getFlow, getFlowNodeFiles } from "../lib/api";
import { fmtRelative, basename } from "../lib/format";

function FolderNode({ data }) {
  const { label, activity_count, active, recent, selected, in_last_session, hot_files } = data;
  const hot = hot_files && hot_files.length > 0;
  return (
    <div
      data-testid={`flow-node-${label}`}
      className={`relative rounded-xl bg-white border ${
        selected ? "border-indigo-400" : active ? "border-[#cbd5e1]" : "border-[#e5e7eb]"
      } px-4 py-3 min-w-[150px] shadow-sm transition-all duration-300 ${
        recent ? "trace-pulse" : ""
      }`}
    >
      {recent && (
        <div className="pointer-events-none absolute -inset-px rounded-xl">
          <div className="absolute inset-0 rounded-xl gradient-border" />
        </div>
      )}
      {in_last_session && (
        <span
          className="absolute -top-2 -right-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-sm font-semibold"
          data-testid={`flow-node-last-${label}`}
        >
          Last active here
        </span>
      )}
      {hot_files && hot_files.length > 0 && (
        <span
          className="absolute -bottom-2 left-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-rose-500 text-white shadow-sm flex items-center gap-1 font-semibold"
          data-testid={`flow-node-hot-${label}`}
          title={hot_files.map((h) => `${h.file_path} (${h.streak} sessions)`).join("\n")}
        >
          <Flame size={9} strokeWidth={2.6} /> Hot zone
        </span>
      )}
      <Handle type="target" position={Position.Left} style={{ background: "#94a3b8" }} />
      <div className="flex items-center gap-2">
        {recent ? (
          <FolderOpen size={16} className="text-indigo-600" strokeWidth={2.2} />
        ) : (
          <Folder
            size={16}
            className={active ? "text-[#0b1220]" : "text-[#6b7280]"}
            strokeWidth={2.0}
          />
        )}
        <span
          className={`text-sm font-medium ${
            active ? "text-[#0b1220]" : "text-[#374151]"
          }`}
        >
          {label}
        </span>
      </div>
      <div
        className={`text-[10.5px] mt-1 font-mono font-medium ${
          activity_count > 0 ? "text-[#4b5563]" : "text-[#6b7280]"
        }`}
      >
        {activity_count > 0 ? `${activity_count} events` : "no activity"}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: "#94a3b8" }} />
    </div>
  );
}

const nodeTypes = { folder: FolderNode };

export default function FlowWorkspace({ projectId }) {
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getFlow(projectId)
      .then((f) => {
        if (!active) return;
        setFlow(f);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [projectId]);

  const recentSet = useMemo(() => {
    if (!flow) return new Set();
    // mark a node "recent" if active in last 7 days
    const sevenDays = Date.now() - 7 * 86400 * 1000;
    return new Set(
      flow.nodes
        .filter(
          (n) => n.last_active_at && new Date(n.last_active_at).getTime() >= sevenDays
        )
        .map((n) => n.key)
    );
  }, [flow]);

  useEffect(() => {
    if (!flow) return;
    setNodes(
      flow.nodes.map((n) => ({
        id: n.key,
        type: "folder",
        position: { x: n.x, y: n.y },
        data: {
          label: n.label,
          activity_count: n.activity_count,
          active: n.active,
          recent: recentSet.has(n.key),
          selected: false,
          last_active_at: n.last_active_at,
          in_last_session: !!n.in_last_session,
          hot_files: n.hot_files || [],
        },
      }))
    );
    setEdges(
      flow.edges.map((e, i) => ({
        id: `${e.source}-${e.target}-${i}`,
        source: e.source,
        target: e.target,
        animated: recentSet.has(e.source) || recentSet.has(e.target),
        style: { stroke: "#d1d5db" },
      }))
    );
  }, [flow, recentSet]);

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

  const onNodeClick = useCallback(
    async (_e, node) => {
      setSelectedNode(node.id);
      setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, selected: n.id === node.id } })));
      // Trace pulse on connected edges
      setEdges((eds) =>
        eds.map((edge) => {
          const connected = edge.source === node.id || edge.target === node.id;
          return connected
            ? { ...edge, className: `${edge.className || ""} traced`.trim() }
            : edge;
        })
      );
      setTimeout(() => {
        setEdges((eds) =>
          eds.map((edge) => ({
            ...edge,
            className: (edge.className || "").replace(/\btraced\b/, "").trim(),
          }))
        );
      }, 950);

      setFilesLoading(true);
      try {
        const data = await getFlowNodeFiles(projectId, node.id);
        setFiles(data.files || []);
      } finally {
        setFilesLoading(false);
      }
    },
    [projectId]
  );

  if (loading) {
    return <div className="trace-bg-grid w-full h-[520px] rounded-xl border border-[#e5e7eb] animate-pulse" />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
      <div
        data-testid="flow-canvas"
        className="relative w-full h-[520px] rounded-xl border border-[#e5e7eb] overflow-hidden trace-bg-grid"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.5}
          maxZoom={1.4}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={18} size={1} color="#e5e7eb" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <aside
        data-testid="flow-context-panel"
        className="rounded-xl border border-[#e5e7eb] bg-white p-5 h-[520px] overflow-y-auto"
      >
        {!selectedNode ? (
          <div className="text-sm text-[#1f2937]">
            <div className="text-[11px] uppercase tracking-wider text-[#4b5563] font-semibold mb-2">
              Folder details
            </div>
            Select a folder to see recent files & activity.
          </div>
        ) : (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#4b5563] font-semibold">
              Folder
            </div>
            <h3 className="text-lg text-[#0b1220] mt-0.5 font-semibold">{selectedNode}</h3>
            <div className="text-xs text-[#4b5563] mt-1 font-medium">
              {files.length} recent files
            </div>
            <div className="mt-4 space-y-2">
              {filesLoading ? (
                <div className="text-sm text-[#4b5563]">Loading…</div>
              ) : files.length === 0 ? (
                <div className="text-sm text-[#6b7280] italic">No recent activity yet.</div>
              ) : (
                files.slice(0, 50).map((f) => (
                  <div
                    key={f.id}
                    className="border border-[#e5e7eb] rounded-md p-2 bg-[#f7f8fa]"
                    title={f.file_path}
                  >
                    <div className="font-mono text-[12px] text-[#0b1220] font-semibold truncate">
                      {basename(f.file_path)}
                    </div>
                    <div className="text-[10.5px] text-[#4b5563] flex justify-between mt-0.5">
                      <span className="truncate">{f.file_path}</span>
                    </div>
                    <div className="text-[10.5px] text-[#6b7280] mt-0.5">
                      {f.event_type} · {fmtRelative(f.event_time)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
